import React from 'react';
import { CommonPopupBodyFooterLayout } from '../../../../global/common-popup/common-popup-body-footer-layout';
import { RolodexContactFormVM } from './rolodex-contact-form.viewmodel';
import { observer } from 'mobx-react';
import classnames from 'classnames';
import { DefaultCloseSubWindowButton } from '../../../../global/sub-window/common/common-close-sub-window-buttons';
import { applyText, cancelText, loadingText, MORE_BUTTON_TEXT } from '../../../../../constants/common-labels.constants';
import { ROLODEX_CONTACT_FORM_CONFIG, RolodexContactFieldKey } from './rolodex-contact-form.config';
import { FormFieldInput } from '../../../../global/form/form-field-input/form-field-input';
import Text from '../../../../global/text/text';
import { ADDITIONAL_NAME_DETAILS_TEXT, NAME_PRONUNCIATION_TEXT, RECORD_AUDIO_TEXT, REMOVE_AUDIO_TEXT, UPLOAD_AUDIO_FILE_TEXT, VIP_LABEL_TEXT } from './rolodex-contact-text';
import OptionsButton from '../../../../global/options-button/options-button';
import PlaySVG from '../../../../../images/svg/play';
import { useAudioPlayer } from '../../../../../infrastructure/hooks/use-audio-player';
import { PLAY_AUDIO_TEXT } from '../upload-audio/uploaded-audio.text';
import { NamePronunciationPopup } from './name-pronunciation-popup/name-pronunciation-popup';


export interface IRolodexContactFormPresentationProps {
    vm: RolodexContactFormVM;
}

const RolodexContactFormPresentation = observer(({ vm }: IRolodexContactFormPresentationProps) => {
    const initialAudioUrl = vm.uploadAudioVM.getAudioSource();
    const { isPlaying, togglePlay } = useAudioPlayer(initialAudioUrl);

    if (vm.loading) {
        return (
            <CommonPopupBodyFooterLayout
                body={
                    <form className='oe-form profile-contact-form'>
                        {loadingText()}
                    </form>
                }
            />
        );
    }

    if (!vm.contactSnapshot) return null;

    const {
        firstName,
        lastName,
        preferredName,
        middleName,
        namePrefix,
        nameSuffix,
        nameNotes,
        namePhonetic,
        title,
        phoneDirect,
        phoneMobile,
        linkedinUrl,
        activeStatus,
        vipFlag
    } = vm.getFormFields(RolodexContactFieldKey);

        const pronunciationOptionsButton = [
            {
                label: vm.intl.formatMessage(RECORD_AUDIO_TEXT),
                action: () => {
                    vm.setNamePronunciationPopupMode('record');
                },
            },
            {
                label: vm.intl.formatMessage(UPLOAD_AUDIO_FILE_TEXT),
                action: () => {
                    vm.setNamePronunciationPopupMode('upload');
                },
            },
            {
                label: vm.intl.formatMessage(REMOVE_AUDIO_TEXT),
                action: vm.onDeleteNamePronunciationAudioFile,
                danger: true,
                disabled: !vm.uploadAudioVM.hasAudio()
            },
        ];

    return <CommonPopupBodyFooterLayout
        body={
            <form className='oe-form profile-contact-form'>
                <div className='oe-form__body'>
                    <div className='oe-form__row'>
                        <FormFieldInput.Text
                            additionalClassName='w-50'
                            field={firstName}
                            labelDescriptor={ROLODEX_CONTACT_FORM_CONFIG[RolodexContactFieldKey.FIRST_NAME].labelDescriptor}
                            withError
                        />
                        <FormFieldInput.Text
                            additionalClassName='w-50'
                            field={lastName}
                            labelDescriptor={ROLODEX_CONTACT_FORM_CONFIG[RolodexContactFieldKey.LAST_NAME].labelDescriptor}
                            withError
                        />
                    </div>
                    <div className='oe-form__row center relative'>
                        <FormFieldInput.Text
                            additionalClassName='w-100 m-right-0px'
                            field={namePhonetic}
                            labelDescriptor={NAME_PRONUNCIATION_TEXT}
                            withError
                        />
                        {initialAudioUrl &&
                            <button
                                type='button'
                                className={classnames('play-icon', { 'active': isPlaying })}
                                onClick={togglePlay}
                                aria-pressed={isPlaying}
                                aria-label={vm.intl.formatMessage(PLAY_AUDIO_TEXT)}

                            >
                                <PlaySVG />
                            </button>
                        }
                        <OptionsButton
                            className='small-button'
                            label={vm.intl.formatMessage(MORE_BUTTON_TEXT)}
                            options={pronunciationOptionsButton}
                            position='top'
                            align='end'
                        />
                    </div>
                    <div className='oe-form__row'>
                        <FormFieldInput.Text
                            additionalClassName='w-100 m-right-0px'
                            field={title}
                            labelDescriptor={ROLODEX_CONTACT_FORM_CONFIG[RolodexContactFieldKey.TITLE].labelDescriptor}
                            withError
                        />
                    </div>
                    <div className='oe-form__row'>
                        <FormFieldInput.Text
                            additionalClassName='w-50'
                            field={phoneDirect}
                            labelDescriptor={ROLODEX_CONTACT_FORM_CONFIG[RolodexContactFieldKey.PHONE_DIRECT].labelDescriptor}
                            withError
                        />
                        <FormFieldInput.Text
                            additionalClassName='w-50'
                            field={phoneMobile}
                            labelDescriptor={ROLODEX_CONTACT_FORM_CONFIG[RolodexContactFieldKey.PHONE_MOBILE].labelDescriptor}
                            withError
                        />
                    </div>
                    <div className='oe-form__row relative'>
                        <FormFieldInput.Text
                            additionalClassName='w-50'
                            field={activeStatus}
                            labelDescriptor={ROLODEX_CONTACT_FORM_CONFIG[RolodexContactFieldKey.ACTIVE_STATUS].labelDescriptor}
                            withError
                        />
                        <FormFieldInput.Toggle
                            className='w-50 relative profile-contact-form__vip-field'
                            isSelected={Boolean(vipFlag.value)}
                            toggle={() => vipFlag.setValue(!vipFlag.value)}
                            text={<Text
                                {...VIP_LABEL_TEXT}
                            />}
                        />
                    </div>
                    <div
                        className='profile-contact-form__free-text'
                    >
                        {vm.intl.formatMessage(ADDITIONAL_NAME_DETAILS_TEXT)}
                    </div>
                    <div className='oe-form__row profile-contact-form__group'>
                        <FormFieldInput.Text
                            additionalClassName='w-15 m-right-0px'
                            field={namePrefix}
                            labelDescriptor={ROLODEX_CONTACT_FORM_CONFIG[RolodexContactFieldKey.NAME_PREFIX].labelDescriptor}
                            placeholder='Dr.'
                            withError
                        />
                        <FormFieldInput.Text
                            additionalClassName='w-70'
                            field={middleName}
                            labelDescriptor={ROLODEX_CONTACT_FORM_CONFIG[RolodexContactFieldKey.MIDDLE_NAME].labelDescriptor}
                            withError
                        />
                        <FormFieldInput.Text
                            additionalClassName='w-15'
                            field={nameSuffix}
                            labelDescriptor={ROLODEX_CONTACT_FORM_CONFIG[RolodexContactFieldKey.NAME_SUFFIX].labelDescriptor}
                            placeholder='|||'
                            withError
                        />
                    </div>
                    <div className='oe-form__row'>
                        <FormFieldInput.Text
                            additionalClassName='w-100 m-right-0px'
                            field={preferredName}
                            labelDescriptor={ROLODEX_CONTACT_FORM_CONFIG[RolodexContactFieldKey.PREFERRED_NAME].labelDescriptor}
                            withError
                        />
                    </div>
                    <div className='oe-form__row'>
                        <FormFieldInput.Text
                            additionalClassName='w-100 m-right-0px'
                            field={nameNotes}
                            labelDescriptor={ROLODEX_CONTACT_FORM_CONFIG[RolodexContactFieldKey.NAME_NOTES].labelDescriptor}
                            withError
                        />
                    </div>
                    <div className='oe-form__row'>
                        <FormFieldInput.Text
                            additionalClassName='w-100 m-right-0px'
                            field={linkedinUrl}
                            labelDescriptor={ROLODEX_CONTACT_FORM_CONFIG[RolodexContactFieldKey.LINKEDIN_URL].labelDescriptor}
                            withError
                        />
                    </div>
                </div>
                <NamePronunciationPopup
                    vm={vm}
                    intl={vm.intl}
                    onClose={vm.closeNamePronunciationPopup}
                    popupMode={vm.namePronunciationPopupMode}
                    contactFields={{
                        firstName: firstName,
                        lastName: lastName,
                        title: title,
                        namePhonetic: namePhonetic,
                        organizationName: vm.contactSnapshot.organizationName,
                    }}
                />
            </form>
        }
        footer={
            <div className='buttons-block buttons-block--from-right'>
                <DefaultCloseSubWindowButton
                    text={applyText()}
                    modifier={['fat', 'confirm-safe']}
                    onClick={vm.submitForm}
                    disabled={vm.submitDisabled}
                />
                <DefaultCloseSubWindowButton text={cancelText()} />
            </div>
        }
    />;
});

export {
    RolodexContactFormPresentation,
};